






















































































import { Component, Vue } from "vue-property-decorator";
import router from "@/router/router";
import { ClientReportRow } from "@/data/models/ClientAdministrationRow";
import {
  getClientReportFileForAdministration,
  cachedClientReportsForAdministration,
  getClientReportsForAdministration,
} from "@/data/repository/ClientAdministrationRepository";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import ContentStateComponent from "@/components/common/ContentState.vue";
import AdminRefreshButtonComponent from "@/components/admin/AdminRefreshButton.vue";
import { EMPTY_VALUE } from "@/utils/ClientAdministrationDetail";
import { setClientPageToolbarTitle } from "@/utils/ClientAdministrationTitle";
import { appendReportPage, reportFileSizeInMb, reportListProjectTitle, reportOpensInTab } from "@/utils/ClientReportList";
import { getFileTypeIconForFileName } from "@/utils/FileTypeIcons";
import { COLOR_LIST_ROW_HOVER_DARK, COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK } from "@/utils/colors";
import { formatFullJsDate } from "@/utils/DateFormatter";
import { formatFloat } from "@/utils/LocalisationHelper";
import { formatReportDateText, formatReportLabel, getReportUsage, isExternalReportUsage } from "@/utils/ReportLabel";
import { ComponentName } from "@/utils/RouteGuards";
import { themed } from "@/utils/Theme";
import { readThroughCache } from "@/utils/AdminReadCache";
import { formatNumber } from "@/utils/Utils";

/**
 * A client's reports in the admin portal, read-only, as IRES Admin's report screen lists them:
 * newest first, a page at a time. Opened from the client detail's Berichte count (all of the
 * client's) or from a row of the Projekte list (`projectId` in the query: that project's only).
 *
 * Each report is named and dated exactly as in a project's Berichte tab (`ReportLabel.ts`), with the
 * year added, since the list spans years.
 *
 * **Not the access boundary**: the data comes from `getClientReportsForAdministration`, which repeats
 * the portal and `MANAGE_CLIENTS` checks server-side and returns metadata only: no storage path, no
 * creator or sender. A click opens the report's file through `getClientReportFileForAdministration`:
 * the browser sends ids only, and IRES Cloud serves the file from the client's own area as a
 * five-minute link and logs the opening (decisions were made on 2026-10-02 (Taimoor): the same
 * callers as the lists, Cloud log only). No filter and no search: the largest client has about
 * 112,000 reports, and both would need new indexes.
 */
@Component({
  components: {
    "content-state": ContentStateComponent,
    "admin-refresh-button": AdminRefreshButtonComponent,
  },
})
export default class ClientAdministrationReportsComponent extends Vue {
  reports: ClientReportRow[] = [];
  total: number | null = null;
  nextCursor: string | null = null;
  isLoading = true;
  /**
   * Showing the cached first page while IRES Cloud is asked again (`AdminReadCache.ts`); "Weitere
   * anzeigen" waits for the fresh page, whose cursor it continues from.
   */
  isRefreshing = false;
  isLoadingMore = false;
  loadError = false;
  /** The report whose link is being fetched, so a second click does not fetch it again; null otherwise. */
  openingReportId: string | null = null;

  readonly emptyValue = EMPTY_VALUE;

  /** The column headings show only above rows. */
  get hasRows(): boolean {
    return !this.isLoading && !this.loadError && this.reports.length > 0;
  }

  get clientId(): string {
    const value = this.$route.query.clientId;
    return typeof value === "string" ? value : "";
  }

  /** Set when opened from a project row; null for the whole client. */
  get projectId(): string | null {
    const value = this.$route.query.projectId;
    return typeof value === "string" && value !== "" ? value : null;
  }

  get isProjectMode(): boolean {
    return this.projectId !== null;
  }

  get projectTitle(): string {
    return this.isProjectMode ? reportListProjectTitle(this.reports) : "";
  }

  get tableHeaderColor() {
    return themed(COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK);
  }

  get hoverStyle(): string {
    const background = themed("", COLOR_LIST_ROW_HOVER_DARK);
    return background ? `background-color: ${background}` : "";
  }

  get totalLabel(): string {
    return this.isLoading || this.total === null ? "–" : formatNumber(this.total);
  }

  reportLabel(report: ClientReportRow): string {
    return formatReportLabel(report, (key, params) => this.$t(key, params).toString());
  }

  /** As in the Berichte tab, with the year: "erstellt 01.10.26 14:30", plus the send date once sent. */
  dateLabel(report: ClientReportRow): string {
    if (report.reportDateMillis === null) {
      return "–";
    }

    return formatReportDateText(
      formatFullJsDate(new Date(report.reportDateMillis)),
      report.lastTimeSendMillis === null ? null : formatFullJsDate(new Date(report.lastTimeSendMillis)),
      isExternalReportUsage(getReportUsage(report)),
      (key, params) => this.$t(key, params).toString()
    );
  }

  sizeLabel(report: ClientReportRow): string {
    const megabytes = reportFileSizeInMb(report);
    return megabytes === null ? "–" : this.$t("admin.clientReports.sizeMb", [formatFloat(megabytes, 2)]).toString();
  }

  fileTypeIcon(report: ClientReportRow): string {
    return getFileTypeIconForFileName(report.fileType ? `file.${report.fileType}` : null);
  }

  /**
   * Opens the report's file: a PDF or image in a new tab, anything else (CSV, JSON) as a download
   * that leaves the list where it is, since IRES Cloud hands those out as downloads.
   *
   * The tab is opened at the click and pointed at the link once IRES Cloud returns it: a tab opened
   * after the wait is no longer a direct response to the click, and browsers block it as a pop-up.
   */
  async openReport(report: ClientReportRow) {
    if (this.openingReportId !== null) {
      return;
    }

    if (!reportOpensInTab(report)) {
      await this.downloadReport(report);
      return;
    }

    const tab = window.open("", "_blank");

    // Blocked anyway: say so, and ask Cloud for nothing, so no opening is logged that never happened.
    if (!tab) {
      SnackbarModule.showError({ key: "admin.clientReports.popupBlocked" });
      return;
    }

    // The report opens in its own context, without a handle back to the portal.
    tab.opener = null;
    this.openingReportId = report.id;

    try {
      tab.location.href = await getClientReportFileForAdministration(this.clientId, report.id);
    } catch (error) {
      tab.close();
      SnackbarModule.showError({ key: this.openErrorKey(error) });
    } finally {
      this.openingReportId = null;
    }
  }

  /** A download needs no tab: the browser saves the file and the portal page stays. */
  async downloadReport(report: ClientReportRow) {
    this.openingReportId = report.id;

    try {
      window.location.assign(await getClientReportFileForAdministration(this.clientId, report.id));
    } catch (error) {
      SnackbarModule.showError({ key: this.openErrorKey(error) });
    } finally {
      this.openingReportId = null;
    }
  }

  openErrorKey(error: unknown): string {
    const code = (error as { code?: unknown } | null)?.code;

    if (code === "not-found" || code === "functions/not-found") {
      return "admin.clientReports.fileMissing";
    }

    return code === "failed-precondition" || code === "functions/failed-precondition"
      ? "admin.clientReports.noFile"
      : "admin.clientReports.openFailed";
  }

  /** Back where it was opened from: the project list for one project, else the client detail. */
  onBack() {
    router.push({
      name: this.isProjectMode ? ComponentName.CLIENT_ADMINISTRATION_PROJECTS : ComponentName.CLIENT_ADMINISTRATION_DETAIL,
      query: { clientId: this.clientId },
    });
  }

  /** The cached first page at once when it was read before in this tab, then IRES Cloud's. */
  async load() {
    const cached = this.clientId === "" ? null : cachedClientReportsForAdministration(this.clientId, this.projectId);
    this.isLoading = cached === null;
    this.isRefreshing = cached !== null;
    this.loadError = false;

    try {
      await readThroughCache(
        cached,
        () =>
          this.clientId === ""
            ? Promise.resolve({ reports: [], nextCursor: null, total: 0 })
            : getClientReportsForAdministration(this.clientId, this.projectId, null),
        (page) => {
          this.reports = page.reports;
          this.nextCursor = page.nextCursor;
          this.total = page.total;
        }
      );
    } catch {
      // Swallowed on purpose: the screen shows its own failure state (a refusal from Cloud
      // included), and the refresh button is the only useful action.
      this.loadError = true;
      this.reports = [];
      this.nextCursor = null;
      this.total = null;
    } finally {
      this.isLoading = false;
      this.isRefreshing = false;
    }
  }

  async loadMore() {
    if (!this.nextCursor || this.isLoadingMore) {
      return;
    }

    this.isLoadingMore = true;

    try {
      const page = await getClientReportsForAdministration(this.clientId, this.projectId, this.nextCursor);
      this.reports = appendReportPage(this.reports, page.reports);
      this.nextCursor = page.nextCursor;
    } catch {
      // The rows already shown stay; the button stays too, so the next page can be retried.
      SnackbarModule.showError({ key: "admin.clientReports.loadMoreFailed" });
    } finally {
      this.isLoadingMore = false;
    }
  }

  mounted() {
    setClientPageToolbarTitle(this.clientId, this.$t("admin.clientReports.title").toString());
    this.load();
  }
}
